import { useSearchParams } from 'react-router';
import { PageShell, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { useTaxInvoiceLabels } from '../components/TaxInvoiceSearchForm';
import { TaxInvoiceRequestsTab } from '../components/TaxInvoiceRequestsTab';
import { TaxInvoiceIssuesTab } from '../components/TaxInvoiceIssuesTab';
import { TaxInvoiceSettingsTab } from '../components/TaxInvoiceSettingsTab';

/*
 * 주문·배송 > 세금계산서 관리 — 고도몰 화면 3개(설정·발행 요청 리스트·발행 내역 리스트)를 탭으로.
 * 탭은 ?tab= 로 보존(딥링크·뒤로가기). 기본 탭은 발행 요청 리스트(운영자가 가장 자주 쓰는 화면).
 */

const TABS = ['requests', 'issues', 'settings'] as const;
type TabKey = (typeof TABS)[number];
const isTab = (v: string | null): v is TabKey => v !== null && (TABS as ReadonlyArray<string>).includes(v);

export const TaxInvoicePage = () => {
  const { t } = useTaxInvoiceLabels();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const active: TabKey = isTab(raw) ? raw : 'requests';
  const label: Record<TabKey, string> = {
    requests: t('orders.taxInvoice.tabs.requests', '발행 요청 리스트'),
    issues: t('orders.taxInvoice.tabs.issues', '발행 내역 리스트'),
    settings: t('orders.taxInvoice.tabs.settings', '세금계산서 설정'),
  };

  return (
    <PageShell
      title={t('orders.taxInvoice.title', '세금계산서 관리')}
      breadcrumb={[{ label: t('orders.taxInvoice.group', '주문·배송') }, { label: t('orders.taxInvoice.title', '세금계산서 관리') }, { label: label[active] }]}
      data-testid="tax-invoice-page"
    >
      <Tabs
        value={active}
        onValueChange={(key) => {
          const next = new URLSearchParams(params);
          next.set('tab', key);
          setParams(next, { replace: true });
        }}
      >
        <TabsList aria-label={t('orders.taxInvoice.title', '세금계산서 관리')}>
          {TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab} data-testid={`tax-invoice-tab-${tab}`}>
              {label[tab]}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* 활성 탭만 마운트 — 쓰지 않는 목록 조회를 막는다. */}
        <TabsContent value="requests">{active === 'requests' ? <TaxInvoiceRequestsTab /> : null}</TabsContent>
        <TabsContent value="issues">{active === 'issues' ? <TaxInvoiceIssuesTab /> : null}</TabsContent>
        <TabsContent value="settings">{active === 'settings' ? <TaxInvoiceSettingsTab /> : null}</TabsContent>
      </Tabs>
    </PageShell>
  );
};
